import React from "react";
import { TrendingUp, MessagesSquare, CalendarCheck, PenLine, Trophy, Percent, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const KPI_DEFS = {
  score: { label: "Athlynx Score", icon: TrendingUp, accent: "text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-500/10" },
  interactions: { label: "Interactions", icon: MessagesSquare, accent: "text-sky-600 bg-sky-50 dark:text-sky-400 dark:bg-sky-500/10" },
  meetings: { label: tfr("Rendez-vous"), icon: CalendarCheck, accent: "text-violet-600 bg-violet-50 dark:text-indigo-400 dark:bg-indigo-500/10" },
  mandates: { label: tfr("Mandats signés"), icon: PenLine, accent: "text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-500/10" },
  placements: { label: "Placements", icon: Trophy, accent: "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-500/10" },
  conversion: { label: tfr("Taux de conversion"), icon: Percent, accent: "text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-500/10" },
  response_time: { label: tfr("Temps de réponse"), icon: Clock, accent: "text-teal-600 bg-teal-50 dark:text-teal-400 dark:bg-teal-500/10" },
};

function deltaText(k) {
  if (k.delta == null) return null;
  if (k.key === "score") return `${k.delta >= 0 ? "+" : ""}${k.delta} pts`;
  if (k.key === "conversion") return `${k.delta >= 0 ? "+" : ""}${k.delta} pts`;
  if (k.deltaPercent != null) return `${k.deltaPercent >= 0 ? "+" : ""}${k.deltaPercent}%`;
  return `${k.delta >= 0 ? "+" : ""}${k.delta}`;
}

/* Direction-aware colouring: backend provides higher_is_better / lower_is_better. */
function deltaTone(k) {
  if (k.delta == null || k.delta === 0) return "text-muted-foreground";
  const improving = k.direction === "lower_is_better" ? k.delta < 0 : k.delta > 0;
  return improving ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400";
}

export default function KpiGrid({ kpis, comparisonLabel, onScoreClick, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="perf-kpi-skeleton">
        {Array.from({ length: 7 }).map((_, i) => <div key={i} className="h-[118px] rounded-2xl bg-muted/60 animate-pulse" />)}
      </div>
    );
  }
  const list = kpis || [];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="perf-kpi-grid">
      {list.map((k, i) => {
        const def = KPI_DEFS[k.key] || {};
        const Icon = def.icon || TrendingUp;
        const dt = deltaText(k);
        const isScore = k.key === "score";
        const isLastOdd = i === list.length - 1 && list.length % 2 === 1;
        const value = isScore ? k.value : (k.display || (k.value != null ? `${k.value}${k.unit || ""}` : "—"));
        const Comp = isScore ? "button" : "div";
        return (
          <Comp
            key={k.key}
            onClick={isScore ? onScoreClick : undefined}
            data-testid={`perf-kpi-${k.key}`}
            className={cn(
              "rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-4 text-left flex flex-col gap-2 min-h-[112px]",
              isLastOdd && "col-span-2 md:col-span-1",
              isScore && "cursor-pointer hover:border-foreground/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
            aria-label={isScore ? `Athlynx Score ${value}. ${tfr("Comprendre mon score")}` : undefined}
          >
            <div className="flex items-center gap-2">
              <span className={cn("h-8 w-8 rounded-xl flex items-center justify-center shrink-0", def.accent)}>
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <span className="text-[11px] text-muted-foreground font-medium leading-tight">{def.label}</span>
            </div>
            <div className="text-2xl font-semibold tabular-nums tracking-tight text-foreground" data-testid={`perf-kpi-value-${k.key}`}>
              {isScore && k.value == null ? <span className="text-sm text-muted-foreground font-normal">{tfr("Calcul en attente")}</span> : value}
              {isScore && k.value != null && <span className="text-xs text-muted-foreground font-normal"> /100</span>}
            </div>
            <div className="text-[11px] tabular-nums min-h-[14px] leading-snug">
              {dt ? (
                <>
                  <span className={cn("font-medium", deltaTone(k))} data-testid={`perf-kpi-delta-${k.key}`}>{dt}</span>
                  {comparisonLabel && (
                    <span className="text-muted-foreground block sm:inline sm:ml-1" title={`vs ${comparisonLabel}`}>{tfr("vs période précédente")}</span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">{comparisonLabel ? tfr("Pas de base de comparaison") : ""}</span>
              )}
            </div>
            {isScore && <span className="text-[10px] text-sky-600 dark:text-sky-400 font-medium">{tfr("Comprendre mon score")} →</span>}
          </Comp>
        );
      })}
    </div>
  );
}
